import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Copy } from "lucide-react";

// Seven DSR request types accepted by /api/dsr.php.
// Labels stay verbatim in both languages to match GDPR / PDPL terminology.
const REQUEST_TYPES = [
  { value: "access",           ar: "حق الاطلاع (المادة 15)",             en: "Right of access (Art. 15)" },
  { value: "rectification",    ar: "حق التصحيح (المادة 16)",              en: "Right to rectification (Art. 16)" },
  { value: "erasure",          ar: "حق المحو (المادة 17)",                en: "Right to erasure (Art. 17)" },
  { value: "restriction",      ar: "حق التقييد (المادة 18)",              en: "Right to restriction (Art. 18)" },
  { value: "objection",        ar: "حق الاعتراض (المادة 21)",             en: "Right to object (Art. 21)" },
  { value: "portability",      ar: "حق نقل البيانات (المادة 20)",         en: "Right to data portability (Art. 20)" },
  { value: "withdraw_consent", ar: "سحب الموافقة (المادة 7(3))",         en: "Withdraw consent (Art. 7(3))" },
];

export default function DataRights() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [form, setForm] = useState({
    name: "", email: "", request_type: "access", details: "", website: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { ref: "DSR-..." }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.details.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/dsr", form);
      if (data?.ok && data?.ref) {
        setResult({ ref: data.ref });
      } else {
        toast.error(isAr ? "تعذّر إرسال الطلب. حاول لاحقاً." : "Could not submit the request. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copyRef = async () => {
    if (!result?.ref) return;
    try {
      await navigator.clipboard.writeText(result.ref);
      toast.success(isAr ? "تم نسخ المرجع" : "Reference copied");
    } catch (_) { /* clipboard blocked — ignore */ }
  };

  return (
    <div data-testid="data-rights-page">
      <section className="bg-navy text-cream py-24">
        <div className="container-x">
          <span className="eyebrow">{isAr ? "خصوصية البيانات" : "Data Privacy"}</span>
          <h1 className="mt-4 text-4xl md:text-6xl">
            {isAr ? "حقوق أصحاب البيانات" : "Data Subject Rights"}
          </h1>
          <span className="divider-gold" />
          <p className="text-cream/80 max-w-3xl leading-relaxed">
            {isAr
              ? "بموجب اللائحة الأوروبية العامة لحماية البيانات (GDPR) ونظام حماية البيانات الشخصية (PDPL)، يحق لك تقديم طلب رسمي لممارسة أي من حقوقك المتعلقة ببياناتك الشخصية. سنرد على طلبك خلال 30 يوماً كحد أقصى (المادة 12(3))."
              : "Under the EU General Data Protection Regulation (GDPR) and the Personal Data Protection Law (PDPL), you may submit a formal request to exercise any of your rights regarding your personal data. We will respond within 30 days at the latest (Art. 12(3))."}
          </p>
        </div>
      </section>

      <section className="container-x py-16 max-w-3xl">
        {result ? (
          <div className="card-elev p-10" data-testid="dsr-success">
            <CheckCircle2 size={44} className="text-gold" />
            <h2 className="mt-4 text-3xl text-navy">
              {isAr ? "تم استلام طلبك" : "Your request has been received"}
            </h2>
            <p className="mt-3 text-navy/80 leading-relaxed">
              {isAr
                ? "احتفظ برقم المرجع أدناه لأي متابعة لاحقة. أرسلنا نسخة إلى بريدك الإلكتروني."
                : "Please keep the reference number below for any follow-up. A copy has been sent to your email."}
            </p>
            <div className="mt-6 flex items-center gap-3 bg-cream/60 border border-gold/40 px-5 py-4">
              <ShieldCheck size={20} className="text-gold" />
              <code data-testid="dsr-ref" className="text-navy text-lg font-mono tracking-wider flex-1">
                {result.ref}
              </code>
              <button
                type="button"
                onClick={copyRef}
                className="text-navy/70 hover:text-gold flex items-center gap-1 text-sm"
                data-testid="dsr-copy-ref"
              >
                <Copy size={16} /> {isAr ? "نسخ" : "Copy"}
              </button>
            </div>
            <p className="mt-6 text-sm text-navy/60">
              {isAr
                ? "سيتواصل معك فريق حوكم عبر البريد الإلكتروني عند معالجة طلبك."
                : "The HawKam team will contact you by email once your request has been processed."}
            </p>
            <button
              type="button"
              className="btn-outline mt-8"
              onClick={() => { setResult(null); setForm({ name: "", email: "", request_type: "access", details: "", website: "" }); }}
              data-testid="dsr-new-request"
            >
              {isAr ? "تقديم طلب جديد" : "Submit another request"}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="card-elev p-8 md:p-10 space-y-5" data-testid="dsr-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input
                required
                data-testid="dsr-name"
                placeholder={isAr ? "الاسم الكامل" : "Full name"}
                className="field-box"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                required
                type="email"
                data-testid="dsr-email"
                placeholder={isAr ? "البريد الإلكتروني" : "Email"}
                className="field-box"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-navy/60 mb-2">
                {isAr ? "نوع الطلب" : "Request type"}
              </label>
              <select
                required
                data-testid="dsr-type"
                className="field-box"
                value={form.request_type}
                onChange={(e) => setForm({ ...form, request_type: e.target.value })}
              >
                {REQUEST_TYPES.map((rt) => (
                  <option key={rt.value} value={rt.value}>
                    {isAr ? rt.ar : rt.en}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              required
              data-testid="dsr-details"
              rows={7}
              maxLength={5000}
              placeholder={isAr
                ? "صف طلبك بالتفصيل — على سبيل المثال البيانات المعنية، الفترة الزمنية، أو الإجراء المطلوب."
                : "Describe your request in detail — e.g. which data is concerned, the time period, or the action requested."}
              className="field-box"
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
            />

            {/* Honeypot — hidden from real users */}
            <input
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true"
            />

            <p className="text-xs text-navy/60 leading-relaxed">
              {isAr
                ? "بإرسال هذا النموذج، فإنك تفهم أن حوكم قد تحتاج إلى التحقق من هويتك قبل معالجة الطلب، وستُعالَج بياناتك وفق سياسة الخصوصية."
                : "By submitting this form, you understand that HawKam may need to verify your identity before processing the request, and your data will be handled according to the Privacy Policy."}
            </p>

            <button
              type="submit"
              disabled={loading}
              data-testid="dsr-submit"
              className="btn-primary w-full md:w-auto"
            >
              {loading
                ? (isAr ? "جاري الإرسال…" : "Submitting…")
                : (isAr ? "إرسال الطلب" : "Submit request")}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
